import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import {
  getCreatorMe,
  submitTextRequirement,
  updateCreatorProfile,
  uploadAvatar,
  uploadFileRequirement,
} from '../lib/api';

export function ProfilePage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['creator-me'], queryFn: getCreatorMe });
  const [form, setForm] = useState<Record<string, string>>({});
  const [reqValues, setReqValues] = useState<Record<string, string>>({});
  const [avatar, setAvatar] = useState<File | null>(null);

  useEffect(() => {
    if (data) {
      setForm({
        displayName: data.creator.displayName,
        ownerName: data.creator.ownerName ?? '',
        bio: data.creator.bio ?? '',
        website: data.creator.links?.[0]?.url ?? '',
      });
    }
  }, [data]);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    meta: { successMessage: 'Profile saved.' },
    mutationFn: async () => {
      await updateCreatorProfile({
        displayName: form.displayName,
        ownerName: form.ownerName || undefined,
        bio: form.bio || null,
        links: form.website ? [{ label: 'Website', url: form.website }] : [],
      });
      if (avatar) await uploadAvatar(avatar);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creator-me'] });
      setAvatar(null);
    },
  });

  const submitText = useMutation({
    meta: { successMessage: 'Submitted.' },
    mutationFn: (key: string) => submitTextRequirement(key, reqValues[key] ?? ''),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['creator-me'] }),
  });
  const submitFile = useMutation({
    meta: { successMessage: 'Uploaded.' },
    mutationFn: ({ key, file }: { key: string; file: File }) => uploadFileRequirement(key, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['creator-me'] }),
  });

  if (!data) return null;
  const subsByKey = new Map(data.submissions.map((s) => [s.requirementKey, s]));

  return (
    <div className="max-w-3xl space-y-4">
      <h1 className="text-2xl font-semibold">Profile</h1>

      <Card>
        <CardHeader>
          <CardTitle>Instructor profile</CardTitle>
          <p className="text-sm text-muted-foreground">
            What learners see on your courses and public page.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-sm font-medium">Display name</label>
              <Input value={form.displayName ?? ''} onChange={set('displayName')} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Account name</label>
              <Input value={form.ownerName ?? ''} onChange={set('ownerName')} />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Bio</label>
            <textarea
              className="w-full rounded-md border bg-background p-2 text-sm min-h-[100px]"
              value={form.bio ?? ''}
              onChange={set('bio')}
              maxLength={4000}
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Website / social link</label>
            <Input type="url" value={form.website ?? ''} onChange={set('website')} />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Photo</label>
            <Input
              type="file"
              accept="image/png,image/jpeg"
              onChange={(e) => setAvatar(e.target.files?.[0] ?? null)}
            />
            <p className="text-[11px] text-muted-foreground">
              PNG or JPEG. {data.creator.avatarKey ? 'A photo is on file.' : 'No photo yet.'}
            </p>
          </div>
          {data.creator.platformFeePct != null && (
            <p className="text-sm text-muted-foreground">
              Platform fee on your sales:{' '}
              <span className="font-medium text-foreground">
                {Number(data.creator.platformFeePct)}%
              </span>{' '}
              — you keep {100 - Number(data.creator.platformFeePct)}% of every course sale.
            </p>
          )}
          <Button onClick={() => save.mutate()} disabled={save.isPending || !form.displayName}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </CardContent>
      </Card>

      {data.requirements.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Requirements</CardTitle>
            <p className="text-sm text-muted-foreground">
              Documents and details the administrator reviews before approval.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {data.requirements.map((r) => {
              const sub = subsByKey.get(r.key);
              return (
                <div key={r.key} className="space-y-1 rounded border p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">
                      {r.label} {r.required && <span className="text-destructive">*</span>}
                    </div>
                    {sub && (
                      <Badge
                        variant={
                          sub.status === 'approved'
                            ? 'default'
                            : sub.status === 'rejected'
                              ? 'destructive'
                              : 'secondary'
                        }
                        className="capitalize"
                      >
                        {sub.status}
                      </Badge>
                    )}
                  </div>
                  {r.description && (
                    <p className="text-[11px] text-muted-foreground">{r.description}</p>
                  )}
                  {sub?.rejectionReason && (
                    <p className="text-xs text-destructive">{sub.rejectionReason}</p>
                  )}
                  {r.acceptsFile ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        accept="image/png,image/jpeg,application/pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) submitFile.mutate({ key: r.key, file });
                        }}
                      />
                      {sub?.fileKey && (
                        <span className="text-xs text-muted-foreground">On file</span>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Input
                        value={reqValues[r.key] ?? sub?.textValue ?? ''}
                        onChange={(e) => setReqValues((v) => ({ ...v, [r.key]: e.target.value }))}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => submitText.mutate(r.key)}
                        disabled={!reqValues[r.key]}
                      >
                        Submit
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
